import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useMobile } from '../utils/useMobile';
import { triggerHaptic } from '../utils/nativeBridge';

const QUICK_AMOUNTS = [10, 20, 50, 100, 200];

const PAYMENT_RAILS = [
  { id: 'wallet', labelEn: 'Wallet', labelAr: 'رصيد المحفظة', icon: 'fa-wallet', subEn: 'Instant & 0% fee', subAr: 'فوري وبدون رسوم' },
  { id: 'cmi', labelEn: 'CMI Card', labelAr: 'بطاقة بنكية', icon: 'fa-credit-card', subEn: 'Moroccan Visa/Mastercard', subAr: 'فيزا وماستركارد مغربية' },
  { id: 'cash_plus', labelEn: 'Cash Plus', labelAr: 'كاش بلوس', icon: 'fa-store', subEn: '3,000+ local agencies', subAr: 'أكثر من 3000 وكالة' },
  { id: 'bank_wallet', labelEn: 'Bank Pay', labelAr: 'تحويل بنكي', icon: 'fa-building-columns', subEn: 'CIH, Attijari, Chaabi', subAr: 'التجاري، الشعبي، CIH' },
];

const DirhamTipModal = ({ isOpen, onClose, recipient, postId = null, onTipSuccess }) => {
  const { t, i18n } = useTranslation();
  const isArabic = (i18n.language || '').toLowerCase().startsWith('ar');
  const { user } = useAuth();
  const toast = useToast();
  const isMobile = useMobile();

  const [wallet, setWallet] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(20);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [paymentRail, setPaymentRail] = useState('wallet');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      axios
        .get('/api/dirham/wallet')
        .then((res) => setWallet(res.data.wallet))
        .catch(() => {});
      setSuccess(false);
    }
  }, [isOpen, user]);

  if (!isOpen || !recipient) return null;

  const tipAmount = customAmount ? parseFloat(customAmount) : selectedAmount;
  const balance = wallet ? parseFloat(wallet.balance) : 0;
  const hasEnoughBalance = paymentRail !== 'wallet' || balance >= tipAmount;

  const handleAmountSelect = (amt) => {
    triggerHaptic('selection');
    setSelectedAmount(amt);
    setCustomAmount('');
  };

  const handleRailSelect = (railId) => {
    triggerHaptic('selection');
    setPaymentRail(railId);
  };

  const handleSendTip = async (e) => {
    e.preventDefault();
    if (!tipAmount || tipAmount <= 0) {
      toast.show(t('dirham.invalidAmount', 'Please enter a valid amount'), 'error');
      return;
    }

    setLoading(true);
    triggerHaptic('medium');

    try {
      const res = await axios.post('/api/dirham/tip', {
        recipient_id: recipient.id,
        post_id: postId,
        amount: tipAmount,
        message: message.trim(),
        payment_rail: paymentRail,
      });

      triggerHaptic('success');
      setSuccess(true);
      toast.show(
        t('dirham.tipSentSuccess', 'Successfully sent {{amount}} MAD to @{{username}}!', {
          amount: tipAmount,
          username: recipient.username,
        }),
        'success'
      );

      if (onTipSuccess) onTipSuccess(res.data.transaction);

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.error || t('dirham.tipFailed', 'Failed to send tip');
      toast.show(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center bg-black/60 backdrop-blur-xs transition-opacity duration-200 ${
        isMobile ? 'items-end p-0' : 'items-center p-4'
      }`}
      onClick={onClose}
    >
      <div
        className={`relative w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl overflow-hidden transition-all duration-300 ${
          isMobile
            ? 'rounded-t-[32px] border-b-0 max-h-[92vh] flex flex-col pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] animate-in slide-in-from-bottom-8'
            : 'max-w-md rounded-3xl p-6 animate-in zoom-in-95'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile iOS/Android Grab Handle */}
        {isMobile && (
          <div className="pt-3 pb-1 flex justify-center">
            <div className="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
          </div>
        )}

        {/* Top Moroccan Geometric Gradient Accent Bar */}
        <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-600" />

        <div className={`overflow-y-auto no-scrollbar ${isMobile ? 'px-5 py-4' : ''}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-black text-sm shadow-sm shadow-emerald-600/30">
                <span>د.م</span>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-black text-zinc-900 dark:text-zinc-100 flex items-center gap-1.5">
                  <span>{t('dirham.title', isArabic ? 'درهم ديالنا' : 'Dirham Dialna')}</span>
                </h3>
                <p className="text-[11px] sm:text-xs text-zinc-500 dark:text-zinc-400">
                  {t('dirham.supportLocally', 'Direct support for Moroccan creators & artisans')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors touch-press"
            >
              <i className="fas fa-times text-sm" />
            </button>
          </div>

          {/* Recipient info card */}
          <div className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200/70 dark:border-zinc-800 mb-4">
            <img
              src={recipient.profile_picture || '/default-avatar.png'}
              alt={recipient.username}
              className="w-11 h-11 rounded-full object-cover border border-zinc-200 dark:border-zinc-700"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zinc-900 dark:text-zinc-100 truncate">
                {recipient.full_name || recipient.username}
              </div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                @{recipient.username}
              </div>
            </div>
            <div className="text-right shrink-0">
              <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-500/20">
                0% Fee
              </span>
            </div>
          </div>

          {success ? (
            <div className="py-8 text-center animate-in zoom-in-95 duration-200">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-3 text-2xl border border-emerald-500/30">
                <i className="fas fa-check" />
              </div>
              <h4 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
                {t('dirham.tipSent', 'Tip Sent! شكراً')}
              </h4>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                {tipAmount} MAD has been transferred directly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSendTip} className="space-y-4">
              {/* Quick Amount Pills */}
              <div>
                <label className="block text-[11px] sm:text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-2">
                  {t('dirham.selectAmount', isArabic ? 'اختر المبلغ (درهم مغربي)' : 'Select Amount (MAD)')}
                </label>
                <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                  {QUICK_AMOUNTS.map((amt) => {
                    const isSelected = selectedAmount === amt && !customAmount;
                    return (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => handleAmountSelect(amt)}
                        className={`py-2 px-1 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold border transition-all touch-press ${
                          isSelected
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-600/20'
                            : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 hover:border-emerald-500/40'
                        }`}
                      >
                        {amt} DH
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Amount input */}
              <div>
                <div className="relative">
                  <input
                    type="number"
                    min="5"
                    max="5000"
                    step="5"
                    placeholder={t('dirham.customAmountPlaceholder', 'Or enter custom amount in MAD...')}
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(0);
                    }}
                    className="w-full h-11 px-4 pr-14 rounded-2xl bg-zinc-50 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none text-zinc-900 dark:text-zinc-100"
                  />
                  <span className="absolute right-4 top-3 text-xs font-bold text-zinc-400">MAD</span>
                </div>
              </div>

              {/* Payment Rail Selection */}
              <div>
                <label className="block text-[11px] sm:text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-2">
                  {t('dirham.paymentRail', isArabic ? 'طريقة الدفع' : 'Payment Rail')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_RAILS.map((rail) => {
                    const isSelected = paymentRail === rail.id;
                    return (
                      <button
                        key={rail.id}
                        type="button"
                        onClick={() => handleRailSelect(rail.id)}
                        className={`p-2.5 rounded-2xl border text-left transition-all touch-press ${
                          isSelected
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-500 text-emerald-800 dark:text-emerald-300 shadow-xs'
                            : 'bg-zinc-50 dark:bg-zinc-800/60 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <i className={`fas ${rail.icon} text-xs text-emerald-600 dark:text-emerald-400`} />
                          <span className="text-[11px] font-bold truncate">{isArabic ? rail.labelAr : rail.labelEn}</span>
                        </div>
                        <div className="text-[10px] text-zinc-500 dark:text-zinc-400 truncate">
                          {isArabic ? rail.subAr : rail.subEn}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message input */}
              <div>
                <textarea
                  rows="2"
                  placeholder={t('dirham.messagePlaceholder', 'Encouragement note (e.g. الله يعطيك الصحة، تبارك الله عليك)...')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={250}
                  className="w-full p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 text-xs sm:text-sm focus:ring-2 focus:ring-emerald-500 outline-none text-zinc-900 dark:text-zinc-100 resize-none"
                />
              </div>

              {/* Balance banner */}
              <div className="p-3 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-500/20 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-zinc-700 dark:text-zinc-300">
                  <i className="fas fa-wallet text-emerald-600 dark:text-emerald-400" />
                  <span>
                    {t('dirham.yourBalance', 'Your Wallet')}:{' '}
                    <strong className="text-emerald-700 dark:text-emerald-300 font-bold">
                      {balance.toFixed(2)} MAD
                    </strong>
                  </span>
                </div>
                {paymentRail === 'wallet' && !hasEnoughBalance && (
                  <button
                    type="button"
                    onClick={() => handleRailSelect('cmi')}
                    className="text-emerald-700 dark:text-emerald-300 font-bold hover:underline"
                  >
                    Use Card Instead
                  </button>
                )}
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading || !hasEnoughBalance}
                className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 active:scale-98 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 transition-all disabled:opacity-50 touch-press"
              >
                {loading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <>
                    <i className="fas fa-paper-plane text-xs" />
                    <span>
                      {t('dirham.confirmTip', isArabic ? 'إرسال {{amount}} د.م دعم' : 'Send {{amount}} MAD Tip', { amount: tipAmount || 0 })}
                    </span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirhamTipModal;
